export type ProfileLink = {
  label: string;
  href: string;
  external?: boolean;
};

export type TopicSection = {
  heading?: string;
  body: string;
};

export type WorkshopProject = {
  slug: string;
  label: string;
};

export type NavLink = {
  label: string;
  href: string;
  external?: boolean;
};

export type Topic = {
  slug: 'play' | 'watch' | 'lab' | 'vote' | 'shop' | 'work' | 'hobbies';
  label: string;
  title: string;
  lede: string;
  description: string;
  sections: TopicSection[];
  projects?: WorkshopProject[];
  links?: ProfileLink[];
  tiles?: NavLink[];
};

export type NavGroup = {
  label: string;
  href?: string;
  children: NavLink[];
};

export type NavItem = NavLink | NavGroup;

export type HomeDoor = {
  label: string;
  href: string;
  featured?: boolean;
};

export function isNavGroup(item: NavItem): item is NavGroup {
  return 'children' in item;
}

export function homeDoors(nav: NavItem[]): HomeDoor[] {
  const featured: HomeDoor[] = [];
  const rest: HomeDoor[] = [];

  for (const item of nav) {
    if (isNavGroup(item)) {
      const href = item.href ?? item.children[0]?.href;
      if (href) {
        featured.push({ label: item.label, href, featured: true });
      }
      for (const child of item.children) {
        rest.push({ label: child.label, href: child.href });
      }
      continue;
    }

    rest.push({ label: item.label, href: item.href });
  }

  return [...featured, ...rest];
}

export type Profile = {
  name: string;
  shortName: string;
  location: string;
  abstract: string;
  floor: string;
  topics: Topic[];
  nav: NavItem[];
  links: ProfileLink[];
};

export const profile: Profile = {
  name: 'Matt Swezey',
  shortName: 'Swezey',
  location: 'Austin',
  abstract: 'I write software in Austin.',
  floor: 'Dog dad.',
  topics: [
    {
      slug: 'play',
      label: 'Play',
      title: 'Flag football and games',
      lede: 'I play both. One outside, one on the couch.',
      description: 'Flag football in Austin, plus Halo, Xbox, and sims.',
      sections: [
        {
          body: 'I play flag football in the Austin Sports and Social League. We won three seasons in a row across 2018 and 2019.',
        },
        {
          body: 'I still play Halo on Xbox. Campaign, multiplayer, whatever’s on.',
        },
        {
          body: 'Shooters if I want something quick. Flight and space sims if I want to sit with it for a while.',
        },
      ],
    },
    {
      slug: 'watch',
      label: 'Watch',
      title: 'Movies',
      lede: 'Star Wars, Marvel, Bond. That’s most of what I rewatch.',
      description: 'Star Wars, Marvel, and James Bond.',
      sections: [
        {
          body: 'I grew up on Star Wars. I still watch it.',
        },
        {
          body: 'Marvel when I want something easy.',
        },
        {
          body: 'Bond when I want something sharp. A suit, a car, and no wasted motion.',
        },
      ],
    },
    {
      slug: 'lab',
      label: 'Lab',
      title: 'Computers',
      lede: 'I’ve been building PCs for a long time.',
      description: 'PCs, a homelab, and some old crypto hardware.',
      sections: [
        {
          body: 'Gaming PCs first. Then racks. Now a homelab that’s never quite finished.',
        },
        {
          body: 'I mined crypto out of the garage for a while. I don’t do that anymore.',
        },
        {
          body: 'I like knowing how the network is set up and where things live.',
        },
      ],
    },
    {
      slug: 'vote',
      label: 'Vote',
      title: 'Politics',
      lede: 'I’m progressive. Equality is a core value.',
      description: 'Progressive. Equality. I live in Texas.',
      sections: [
        {
          body: 'I live in Texas. I vote progressive.',
        },
        {
          body: 'Equality is at the center of that. Housing, healthcare, and policy that lets people stay and get a fair shot.',
        },
      ],
    },
    {
      slug: 'shop',
      label: 'Workshop',
      title: 'Cars and DIY',
      lede: 'I like fixing things.',
      description: 'DIY, cars, and a Rivian that isn’t stock.',
      sections: [
        {
          body: 'Cars, parts, home projects. If it comes apart, I’ll usually open it.',
        },
        {
          body: 'The Rivian is what I’m working on now. I’ve changed things on it.',
        },
      ],
      projects: [
        { slug: 'challenger', label: '’15 Challenger SXT+ v6 Supercharged' },
        { slug: 'ram', label: '’17 RAM Sport CC' },
        { slug: 'flooring', label: 'House Flooring Remodel' },
        { slug: 'home-server', label: 'Home Server Build' },
        { slug: 'home-office', label: 'Home Office Remodel' },
        { slug: 'gaming-pc', label: 'Gaming Computer Build' },
      ],
    },
  ],
  nav: [
    {
      label: 'Hobbies',
      href: '/hobbies',
      children: [
        { label: 'Play', href: '/play' },
        { label: 'Watch', href: '/watch' },
        { label: 'Workshop', href: '/shop' },
      ],
    },
    { label: 'Work', href: '/work' },
    { label: 'Values', href: '/vote' },
  ],
  links: [
    { label: 'Blog', href: 'https://blog.swezey.io/', external: true },
    { label: 'GitHub', href: 'https://github.com/mswezey23', external: true },
    {
      label: 'LinkedIn',
      href: 'https://www.linkedin.com/in/swezey-matt',
      external: true,
    },
    { label: 'Twitter', href: 'https://x.com/007_Swezey_117', external: true },
  ],
};

export const hobbies: Topic = {
  slug: 'hobbies',
  label: 'Hobbies',
  title: 'Hobbies',
  lede: '',
  description: 'Play, Watch, and Workshop.',
  sections: [],
  tiles: [
    { label: 'Play', href: '/play' },
    { label: 'Watch', href: '/watch' },
    { label: 'Workshop', href: '/shop' },
  ],
};

export const work: Topic = {
  slug: 'work',
  label: 'Work',
  title: 'Software',
  lede: 'I write software in Austin.',
  description: 'Matt Swezey writes software in Austin.',
  sections: [
    {
      body: 'I write software in Austin.',
    },
  ],
  links: profile.links.filter((link) => link.label === 'GitHub' || link.label === 'LinkedIn'),
};

export function topicBySlug(slug: string): Topic | undefined {
  if (slug === hobbies.slug) {
    return hobbies;
  }
  if (slug === work.slug) {
    return work;
  }
  return profile.topics.find((topic) => topic.slug === slug);
}

export function topicPath(slug: Topic['slug']): string {
  return `/${slug}`;
}

export function projectPath(topicSlug: Topic['slug'], projectSlug: string): string {
  return `/${topicSlug}/${projectSlug}`;
}

export function projectBySlug(topic: Topic, slug: string): WorkshopProject | undefined {
  return topic.projects?.find((project) => project.slug === slug);
}
